import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, ResponsiveContainer, Tooltip } from 'recharts'

interface RadarChartComponentProps {
  data: Array<Record<string, any>>
  subjectKey: string
  valueKeys: string[]
  /** Etiquetas para mostrar en leyenda/eje en lugar del nombre del campo */
  valueKeyLabels?: Record<string, string>
  title?: string
  colors?: string[]
  fillOpacity?: number
  loading?: boolean
}

// Paleta de colores por defecto
const DEFAULT_COLORS = [
  '#10b981', // Verde
  '#3b82f6', // Azul
  '#f59e0b', // Naranja
  '#ef4444', // Rojo
  '#8b5cf6', // Morado
  '#14b8a6', // Teal
]

export function RadarChartComponent({
  data,
  subjectKey,
  valueKeys,
  valueKeyLabels,
  title,
  colors = DEFAULT_COLORS,
  fillOpacity = 0.3,
  loading = false,
}: RadarChartComponentProps) {
  if (loading) {
    return (
      <div className="bg-white border rounded-lg p-6">
        {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}
        <div className="h-[400px] flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white border rounded-lg p-6">
        {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}
        <div className="h-[400px] flex items-center justify-center text-gray-500">
          No hay datos disponibles
        </div>
      </div>
    )
  }

  // Transformar datos para radar chart
  const radarData = data.map((item) => {
    const row: Record<string, any> = {
      subject: String(item[subjectKey] || 'Sin nombre'),
    }

    valueKeys.forEach((key) => {
      row[key] = Number(item[key]) || 0
    })

    return row
  })

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const subject = payload[0].payload.subject

      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
          <p className="font-semibold text-gray-900 mb-2">{subject}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm text-gray-600">
              <span className="inline-block w-3 h-3 rounded-full mr-2" style={{ backgroundColor: entry.color }}></span>
              {entry.name}: <span className="font-medium">{entry.value.toLocaleString('es-ES')}</span>
            </p>
          ))}
        </div>
      )
    }
    return null
  }

  return (
    <div className="bg-white border rounded-lg p-6">
      {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}

      <ResponsiveContainer width="100%" height={400}>
        <RadarChart data={radarData}>
          <PolarGrid stroke="#e5e7eb" />
          <PolarAngleAxis
            dataKey="subject"
            tick={{ fill: '#6b7280', fontSize: 12 }}
          />
          <PolarRadiusAxis
            angle={90}
            tick={{ fill: '#6b7280', fontSize: 11 }}
            tickFormatter={(value) => value.toLocaleString('es-ES')}
          />

          {/* Renderizar múltiples radares */}
          {valueKeys.map((key, index) => (
            <Radar
              key={key}
              name={valueKeyLabels?.[key] ?? key}
              dataKey={key}
              stroke={colors[index % colors.length]}
              fill={colors[index % colors.length]}
              fillOpacity={fillOpacity}
              strokeWidth={2}
            />
          ))}

          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ fontSize: '12px' }}
            iconType="circle"
          />
        </RadarChart>
      </ResponsiveContainer>

      {/* Info adicional */}
      <div className="mt-4 text-xs text-gray-500 text-center">
        Gráfico radial: Comparación multidimensional de {valueKeys.length} métricas
      </div>
    </div>
  )
}
